import { AgChartTheme } from 'ag-charts-community';

/**
 * Constrói um AgChartTheme a partir dos tokens --tem-* computados.
 *
 * O tema é aplicado SOMENTE no objeto de options derivado em runtime
 * (chart-card.chartOptions) — nunca gravado em item.options, que é o
 * formato persistido em Mktpdashboard.EXPRESSION. Opções explícitas do
 * usuário no JSON continuam vencendo o theme (semântica do ag-charts).
 *
 * @param host elemento dentro do escopo do tema (para o dark do
 *             .theme-alternate resolver os tokens corretos).
 */
export function buildChartTheme(host?: HTMLElement): AgChartTheme {
    const styles = getComputedStyle(host ?? document.documentElement);
    const token = (name: string, fallback: string) =>
        (styles.getPropertyValue(name) || '').trim() || fallback;

    const deep = token('--tem-deep', '#134465');
    const accent = token('--tem-accent', '#2ec5a2');
    const warn = token('--tem-warn', '#e0a13c');
    const success = token('--tem-success', '#2ea06b');
    const danger = token('--tem-danger', '#d4544f');
    const surface = token('--tem-surface', '#ffffff');
    const border = token('--tem-border', '#e3eaef');
    const ink = token('--tem-ink', '#12283a');
    const muted = token('--tem-muted', '#5b7285');
    const fontFamily = token('--tem-font-family', 'Roboto, sans-serif');

    // paleta categórica derivada da marca
    const fills = [
        deep,
        accent,
        warn,
        success,
        danger,
        mix(deep, 55),
        mix(accent, 55),
        mix(warn, 55),
    ];

    return {
        palette: {
            fills,
            strokes: fills,
        },
        overrides: {
            common: {
                background: { fill: surface },
                title: { color: ink, fontFamily },
                subtitle: { color: muted, fontFamily },
                legend: {
                    item: { label: { color: muted, fontFamily } },
                },
                axes: {
                    category: {
                        label: { color: muted, fontFamily },
                        line: { stroke: border },
                    },
                    number: {
                        label: { color: muted, fontFamily },
                        gridLine: { style: [{ stroke: border }] },
                    },
                },
            },
        },
    } as AgChartTheme;
}

/** clareia uma cor hex misturando com branco (percent = quanto da cor original mantém) */
function mix(hex: string, percent: number): string {
    const clean = hex.replace('#', '');
    if (clean.length !== 6) {
        return hex;
    }
    const channel = (i: number) => {
        const value = parseInt(clean.substring(i * 2, i * 2 + 2), 16);
        const mixed = Math.round(value * (percent / 100) + 255 * (1 - percent / 100));
        return mixed.toString(16).padStart(2, '0');
    };
    return `#${channel(0)}${channel(1)}${channel(2)}`;
}
